import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';

// Define the Hero component
const Hero = ({
  title = 'Welcome to our site',
  subtitle = 'A brief description of what we do',
  backgroundImage = '',
  buttonText = 'Learn More',
  buttonLink = '#',
  overlayColor = 'rgba(0, 0, 0, 0.5)',
  textColor = '#ffffff',
  height = '500px',
  align = 'center'
}) => {
  console.log('Rendering Hero component with props:', { title, subtitle, backgroundImage });
  
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
        color: textColor,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: overlayColor,
          zIndex: 1
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: align }}>
        <Typography variant="h2" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h5" paragraph>
          {subtitle}
        </Typography>
        {buttonText && (
          <Button 
            variant="contained" 
            color="primary" 
            href={buttonLink}
            size="large"
          >
            {buttonText}
          </Button>
        )}
      </Container>
    </Box>
  );
};

// Define the component's fields for the Puck editor
// Updated for Puck v0.18.2 compatibility with standard field types
Hero.fields = {
  title: {
    type: 'text',
    label: 'Title',
    default: 'Welcome to our site',
    description: 'The main heading displayed in the hero section'
  },
  subtitle: {
    type: 'text',
    label: 'Subtitle',
    default: 'A brief description of what we do',
    description: 'A brief description that appears below the title'
  },
  backgroundImage: {
    type: 'text',
    label: 'Background Image URL',
    default: '',
    description: 'URL to the background image (leave empty for no image)'
  },
  buttonText: {
    type: 'text',
    label: 'Button Text',
    default: 'Learn More',
    description: 'Text displayed on the call-to-action button (leave empty to hide button)'
  },
  buttonLink: {
    type: 'text',
    label: 'Button Link',
    default: '#',
    description: 'URL the button links to when clicked'
  },
  overlayColor: {
    type: 'text',
    label: 'Overlay Color',
    default: 'rgba(0, 0, 0, 0.5)',
    description: 'Color overlay applied on top of the background image'
  },
  textColor: {
    type: 'text',
    label: 'Text Color',
    default: '#ffffff',
    description: 'Color of the text in the hero section'
  },
  height: {
    type: 'text',
    label: 'Height',
    default: '500px',
    description: 'Height of the hero section (e.g., 500px, 50vh)'
  },
  align: {
    type: 'select',
    label: 'Alignment',
    options: [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' }
    ],
    default: 'center',
    description: 'Horizontal alignment of the content within the hero section'
  }
};

// Define the component's display properties for the editor
Hero.componentOptions = {
  // Component preview in the editor
  preview: () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: '#f5f5f5', 
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100px'
    }}>
      <Typography variant="subtitle1" fontWeight="bold">Hero Section</Typography>
      <Typography variant="caption">Full-width banner with title, subtitle, and CTA</Typography>
    </Box>
  ),
  // Category for component organization
  category: 'Layout',
  // Icon for the component in the editor
  icon: '🏆'
};

// Make sure we're exporting the component properly
console.log('Hero component defined:', !!Hero);

// Export the component
export default Hero; 